import { _getLogger } from '@doxyme/analytics';
import { attachHandler, Specs } from '@doxyme/extensions-common';

import ExtensionCapability from '../../capability';
import type Instance from '../../instance';

type Constraints = Specs.FeatureFlag.Constraints;
type GetPayload = Specs.FeatureFlag.GetPayload;

const { FeatureFlag } = Specs;

type GetHandler = (feature: string) => Promise<unknown>;

const logger = _getLogger('featureFlag', {
  component: 'extensions',
  lib: 'bridge',
});

/**
 * FeatureFlag allows extensions to check if a feature flag is enabled.
 */
export default class FeatureFlagCapability extends ExtensionCapability<Constraints> {
  static readonly id = FeatureFlag.id;

  protected readonly getHandlers: GetHandler[] = [];

  constructor() {
    super(FeatureFlag);

    this.handlers.push([FeatureFlag.Actions.Get, this.#get.bind(this)]);
  }

  /**
   * "Get" action handler, gets the value of a feature flag
   */
  async #get(
    payload: GetPayload,
    instance: Instance<Constraints>,
  ): Promise<void> {
    if (!this.#isAllowedFeature(payload.feature, instance)) {
      return;
    }

    let value: unknown;
    for (const handler of this.getHandlers) {
      value = await handler(payload.feature);
    }

    instance.notify([
      FeatureFlag.Events.GetResponse,
      { feature: payload.feature, value },
    ]);
  }

  /**
   * Attach a handler to the feature flag capability
   * @param handler - The handler to attach
   * @returns A function to detach the handler
   */
  onGet(handler: GetHandler): () => void {
    return attachHandler<GetHandler>(this.getHandlers, handler);
  }

  #isAllowedFeature(feature: string, instance: Instance): boolean {
    const isValid = this.configsBy(instance).some((config) => {
      return config.features?.includes(feature) ?? false;
    });
    if (!isValid) {
      logger.warn(`Rejecting action for unregistered feature: ${feature}`, {
        feature,
        manifest: {
          id: instance.manifest.id,
          name: instance.manifest.name,
        },
      });
    }
    return isValid;
  }
}
